import { FaShoppingBag, FaUtensils } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setOrderType } from "../../Redux/orderSlice";
import { useEffect } from "react";
import { getUserdata } from "../../helper/generalHelper";

// Alternative Compact Version
const HomeScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isStaffRoute = window.location.pathname.includes('/restaurant-staff');
  
  let userPermissions = getUserdata("permissions") || [];
  if (typeof userPermissions === "string") {
    try { userPermissions = JSON.parse(userPermissions); } catch(e) { userPermissions = []; }
  }
  
  const hasPerm = (id) => {
    return Array.isArray(userPermissions) && userPermissions.some(p => Number(p) === id);
  };

  // Legacy mode: if they have Counter Access (123) but no sub-permissions, give full access so we don't break existing setups
  const legacyMode = isStaffRoute && hasPerm(123) && !hasPerm(191) && !hasPerm(192) && !hasPerm(193);

  const showTakeaway = !isStaffRoute || legacyMode || hasPerm(191);
  const showDineIn = !isStaffRoute || legacyMode || hasPerm(192);
  const showQSR = !isStaffRoute || legacyMode || hasPerm(193);

  const orderTypes = [];

  if (showTakeaway) {
    orderTypes.push({
      type: "takeaway",
      title: "Takeaway",
      icon: FaShoppingBag,
      color: "green",
      shortcut: "T"
    });
  }

  if (showDineIn) {
    orderTypes.push({
      type: "dinein",
      title: "Dine In",
      icon: FaUtensils,
      color: "blue",
      shortcut: "D"
    });
  }

  if (showQSR) {
    orderTypes.push({
      type: "QSR",
      title: "QSR",
      icon: FaUtensils,
      color: "blue",
      shortcut: "Q"
    });
  }

  const handleOrderTypeSelect = (type) => {
    dispatch(setOrderType(type));
    
    // Dynamically determine the base route to avoid triggering 401 logouts
    const isStaffRoute = window.location.pathname.includes('/restaurant-staff');
    const basePath = isStaffRoute ? '/restaurant-staff/employee' : '/restaurant';

    if (type === "takeaway") {
      navigate(`${basePath}/create-order?type=${type}`);
    } else if (type === "dinein") {
      navigate(`${basePath}/table-selection`);
    } else {
      navigate(`${basePath}/create-order?type=${type}`);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.key === 't' || e.key === 'T') && showTakeaway) {
        handleOrderTypeSelect('takeaway');
      } else if ((e.key === 'd' || e.key === 'D') && showDineIn) {
        handleOrderTypeSelect('dinein');
      } else if ((e.key === 'q' || e.key === 'Q') && showQSR) {
        handleOrderTypeSelect('QSR');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showTakeaway, showDineIn, showQSR]);

  return (
    <div className="min-h-screen  flex flex-col justify-center items-center p-4">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center shadow-2xl mb-6 mx-auto">
          <FaUtensils className="text-3xl text-white" />
        </div>
        <h1 className="text-4xl font-bold text-primary mb-2">Restaurant POS</h1>
        <p className="text-gray-400">Select order type to begin</p>
      </div>

      {/* Order Type Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-2xl">
        {orderTypes.map((orderType) => {
          const IconComponent = orderType.icon;
          const colorClasses = {
            green: 'bg-primary ',
            blue: 'bg-primary'
          };

          return (
            <button
              key={orderType.type}
              onClick={() => handleOrderTypeSelect(orderType.type)}
              className={`group relative bg-gradient-to-r ${colorClasses[orderType.color]} rounded-2xl p-8 text-white shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-2xl active:scale-95`}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-opacity-30 transition-all">
                  <IconComponent className="text-2xl" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{orderType.title}</h3>
                <p className="text-blue-100 text-sm opacity-90">Press '{orderType.shortcut}'</p>
              </div>
              
              {/* Keyboard shortcut hint */}
              <div className="absolute top-4 right-4 w-8 h-8 bg-black bg-opacity-20 rounded-lg flex items-center justify-center text-xs font-mono">
                {orderType.shortcut}
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer Tips */}
      <div className="mt-12 text-center text-gray-400 text-sm">
        <p>💡 Tip: Use keyboard shortcuts for faster access</p>
      </div>
    </div>
  );
};

export default HomeScreen;